import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { ms, s, vs } from '../utils/responsive';
import { useNavigation } from '@react-navigation/native';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useUserSelection } from '../contexts/UserSelectionContext';
import { createLogger } from '../utils/logger';
import { TAP } from '../utils/pointerWords';

const logger = createLogger('UpgradeBanner');

interface UpgradeBannerProps {
  variant?: 'compact' | 'full';  // compact for checklist screens, full for dashboard
}

/**
 * UpgradeBanner - Shows when user needs to subscribe/upgrade to access their own data
 *
 * This banner appears for:
 * - Users who signed up as successor/keyholder but haven't started their own trial
 * - Users whose trial has expired
 * - Users who cancelled their subscription
 *
 * It does NOT appear when:
 * - User is viewing another user's data as successor/keyholder (that's always allowed)
 * - User has an active subscription or trial
 */
export const UpgradeBanner: React.FC<UpgradeBannerProps> = ({ variant = 'full' }) => {
  const navigation = useNavigation();
  const { canViewOwnData, currentTier, isLoading, hasStartedOwnTrial } = useSubscription();
  const { isViewingOthersData } = useUserSelection();

  logger.debug('canViewOwnData:', canViewOwnData, 'isViewingOthersData:', isViewingOthersData, 'isLoading:', isLoading, 'currentTier:', currentTier, 'hasStartedOwnTrial:', hasStartedOwnTrial);

  // Don't show banner while loading subscription status
  if (isLoading) {
    logger.debug('Still loading, not showing banner');
    return null;
  }

  // Don't show banner if:
  // 1. User can view their own data (active subscription/trial)
  // 2. User is viewing someone else's data (successor/keyholder view)
  if (canViewOwnData || isViewingOthersData) {
    logger.debug('Not showing banner - canViewOwnData:', canViewOwnData, 'isViewingOthersData:', isViewingOthersData);
    return null;
  }

  logger.debug('SHOWING BANNER - user needs to subscribe');

  const handleSubscribe = () => {
    (navigation as any).navigate('Profile', { screen: 'SubscriptionManagement' });
  };

  // Use different messaging based on whether user has started a trial
  // - New users who never started a trial: encourage starting free trial
  // - Users whose trial expired: encourage upgrading
  // - Users who cancelled their subscription: encourage resubscribing
  const title = hasStartedOwnTrial ? 'Subscription Expired' : 'Start Your Free Trial';
  const message = hasStartedOwnTrial
    ? 'Your subscription has expired. Subscribe to continue accessing your personal data.'
    : 'Start your 14-day free trial to begin your end-of-life planning.';
  const buttonText = hasStartedOwnTrial ? 'Subscribe' : 'Start Free Trial';
  const compactMessage = hasStartedOwnTrial ? `${TAP} to subscribe` : `${TAP} to start free trial`;

  if (variant === 'compact') {
    return (
      <TouchableOpacity
        style={styles.compactContainer}
        onPress={handleSubscribe}
        accessibilityRole="button"
        accessibilityLabel={`${title}. ${compactMessage}`}
        accessibilityHint="Double tap to go to subscription options"
      >
        <View style={styles.compactContent}>
          <Text style={styles.compactIcon} accessibilityElementsHidden={true}>🔒</Text>
          <View style={styles.compactTextContainer}>
            <Text style={styles.compactTitle}>{title}</Text>
            <Text style={styles.compactMessage} numberOfLines={1}>
              {compactMessage}
            </Text>
          </View>
          <Text style={styles.compactArrow} accessibilityElementsHidden={true}>›</Text>
        </View>
      </TouchableOpacity>
    );
  }

  return (
    <View
      style={styles.container}
      accessibilityRole="alert"
      accessibilityLabel={`${title}. ${message}`}
    >
      <View style={styles.iconContainer} accessibilityElementsHidden={true}>
        <Text style={styles.icon}>🔒</Text>
      </View>
      <Text style={styles.title}>{title}</Text>
      <Text style={styles.message}>{message}</Text>
      <TouchableOpacity
        style={styles.upgradeButton}
        onPress={handleSubscribe}
        accessibilityRole="button"
        accessibilityLabel={buttonText}
        accessibilityHint="Double tap to view subscription options"
      >
        <Text style={styles.upgradeButtonText}>{buttonText}</Text>
      </TouchableOpacity>
      <Text style={styles.trialNote} accessibilityRole="text">
        Try any plan free for 14 days. Cancel anytime.
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  // Full variant styles
  container: {
    backgroundColor: '#FFF3E0',
    borderRadius: ms(15),
    padding: ms(20),
    marginHorizontal: ms(15),
    marginVertical: ms(10),
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#FFB74D',
  },
  iconContainer: {
    width: s(50),
    height: s(50),
    borderRadius: ms(25),
    backgroundColor: '#FFE0B2',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: ms(12),
  },
  icon: {
    fontSize: ms(24, 0.3),
  },
  title: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#E65100',
    marginBottom: ms(8),
    textAlign: 'center',
  },
  message: {
    fontSize: ms(14, 0.3),
    color: '#666',
    textAlign: 'center',
    marginBottom: ms(16),
    lineHeight: ms(20, 0.3),
  },
  upgradeButton: {
    backgroundColor: '#FF9800',
    paddingVertical: ms(12),
    paddingHorizontal: ms(30),
    borderRadius: ms(25),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  upgradeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  trialNote: {
    fontSize: ms(12, 0.3),
    color: '#999',
    marginTop: ms(10),
  },

  // Compact variant styles
  compactContainer: {
    backgroundColor: '#FFF3E0',
    borderRadius: ms(10),
    marginHorizontal: ms(15),
    marginVertical: ms(8),
    borderWidth: 1,
    borderColor: '#FFB74D',
  },
  compactContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(12),
  },
  compactIcon: {
    fontSize: ms(20, 0.3),
    marginRight: ms(10),
  },
  compactTextContainer: {
    flex: 1,
  },
  compactTitle: {
    fontSize: ms(14, 0.3),
    fontWeight: 'bold',
    color: '#E65100',
  },
  compactMessage: {
    fontSize: ms(12, 0.3),
    color: '#666',
  },
  compactArrow: {
    fontSize: ms(24, 0.3),
    color: '#E65100',
    fontWeight: '300',
  },
});

export default UpgradeBanner;
